import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from './Modal';
import ModalContent from './ModalContent';
import ModalCloseButton from './ModalCloseButton';
import Button from './Button';
import { FormField, Select, Textarea } from './Form';
import { Heading, Text } from './Typography';
import { submitSiteFeedback, validateSiteFeedbackInput, SITE_FEEDBACK_CATEGORIES } from '../../services/feedbackService';
import { getFeedbackPageLabel } from '../../utils/feedbackPageLabel';

const MESSAGE_MAX = 2000;

const SiteFeedbackModal = ({ isOpen, onClose, currentUser }) => {
  const location = useLocation();
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const reset = useCallback(() => {
    setCategory('general');
    setMessage('');
    setError(null);
    setSubmitting(false);
    setSuccess(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      reset();
    }
  }, [isOpen, reset]);

  if (!isOpen) return null;

  const pagePath = location?.pathname || '';
  const pageLabel = getFeedbackPageLabel(pagePath);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!currentUser?.id) {
      setError('You must be signed in to send feedback.');
      return;
    }
    const v = validateSiteFeedbackInput({ category, message });
    if (v.error) {
      setError(v.error);
      return;
    }
    setSubmitting(true);
    try {
      await submitSiteFeedback({
        category,
        message: message.trim(),
        pagePath,
        pageLabel,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      });
      setSuccess(true);
      window.setTimeout(() => {
        onClose();
        reset();
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!submitting}>
      <ModalContent size="medium" className="relative max-h-[90vh] overflow-y-auto">
        <ModalCloseButton onClick={onClose} disabled={submitting} />
        {success ? (
          <div className="pt-2 text-center">
            <Heading level={3} className="mb-2 text-text-primary">
              Thank you
            </Heading>
            <Text className="text-text-secondary">Your feedback was sent.</Text>
          </div>
        ) : (
          <>
            <Heading level={3} className="mb-1 pr-10 text-text-primary">
              Send feedback
            </Heading>
            <Text className="mb-4 text-sm text-text-secondary">
              Help us improve Shuttlr. We record which area of the app you were on.
            </Text>
            {pagePath && (
              <div className="mb-4 rounded-md border border-border-medium bg-surface-subtle px-3 py-2 text-xs">
                <p className="text-text-primary">
                  <span className="text-text-muted">Page: </span>
                  <span className="font-medium">{pageLabel}</span>
                </p>
                <p className="mt-1.5 text-text-muted">
                  The full URL is stored for our team but hidden here so the form stays readable.
                </p>
              </div>
            )}
            {error && (
              <div className="mb-4 rounded-lg border border-danger/30 bg-danger-light px-3 py-2 text-sm text-danger-dark">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <FormField label="Category" required>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  options={SITE_FEEDBACK_CATEGORIES}
                  placeholder=""
                />
              </FormField>
              <FormField
                label="Message"
                required
                helperText={`${message.trim().length} / ${MESSAGE_MAX} characters`}
              >
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  placeholder="What happened? What would you like to see?"
                  maxLength={MESSAGE_MAX}
                  disabled={submitting}
                />
              </FormField>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Submit feedback'}
                </Button>
              </div>
            </form>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SiteFeedbackModal;
